import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api.js";
import type { Profile, Project } from "../lib/types.js";

export function useRunProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (projectId: string) => {
      const { data } = await api.post<{ runId: string }>(`/projects/${projectId}/run`, {});
      return data;
    },
    onSuccess: (_data, projectId) => {
      void qc.invalidateQueries({ queryKey: ["runs", projectId] });
      void qc.invalidateQueries({ queryKey: ["projects", projectId] });
    },
  });
}

export function useCreateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; checks: Record<string, unknown> }) => {
      const { data } = await api.post<Profile>("/profiles", body);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["profiles"] });
    },
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, config }: { id: string; config: Record<string, unknown> }) => {
      const { data } = await api.put<Profile>(`/profiles/${id}`, { checks: config });
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["profiles"] });
    },
  });
}

export function useDeleteProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/profiles/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["profiles"] });
    },
  });
}

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name: string;
      url_base: string;
      profile: string;
      schedule: string | null;
      active: boolean;
      url_types: { portada: { url: string } };
      overrides: Record<string, unknown>;
    }) => {
      const { data } = await api.post<Project>("/projects", body);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}
